import { CommonModule } from '@angular/common';
import { Component, Input, NgModule, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { Observable, Subject } from 'rxjs';
import { shareReplay, takeUntil } from 'rxjs/operators';
import { AuxiliaryDataModule } from '../../auxiliary-data.module';
import { FieldHandler } from '../auxiliary-data-table/auxiliary-data-table.interface';
import { AuxiliaryFieldHandlerService, LoadingStatus } from '../auxiliary-field-handler.service';
import { ObjectType } from '../schema-service/schema-interface';

export const SHOW_ALL_FIELDS = 100;

@Component({
  selector: 'aux-auxiliary-fields',
  template: `
    <ng-container *ngIf="fieldHandlers$ | async as fieldHandlers; else loadingState">
      <form *ngIf="fieldHandlers.length > 0" [formGroup]="formGroup">
        <ng-container *ngFor="let handler of fieldHandlers; let i = index">
          <ng-container *ngIf="i < numberOfFieldsToShow">
            <aux-renderer-selector [handler]="handler"></aux-renderer-selector>
          </ng-container>
        </ng-container>
      </form>

      <glxy-empty-state *ngIf="fieldHandlers.length === 0">
        <glxy-empty-state-hero>
          <mat-icon class="empty-state-icon">{{ emptyStateIcon }}</mat-icon>
        </glxy-empty-state-hero>
        <glxy-empty-state-title *ngIf="emptyStateTitle">
          {{ emptyStateTitle | translate }}
        </glxy-empty-state-title>
        <p class="empty-state-text" *ngIf="emptyStateText">
          {{ emptyStateText | translate }}
        </p>
        <glxy-empty-state-actions>
          <a
            data-action="clicked-manage-custom-fields-empty-table"
            mat-stroked-button
            [href]="auxiliaryDataAdminURL"
            *ngIf="auxiliaryDataAdminURL"
          >
            {{ 'AUXILIARY_DATA_TABLE.EMPTY.BUTTON' | translate }}
          </a>
        </glxy-empty-state-actions>
      </glxy-empty-state>
    </ng-container>
    <ng-template #loadingState>
      <ng-container *ngIf="(loadingStatus$ | async) === LoadingStatus.LOADING">
        <div class="loading-container">
          <mat-spinner diameter="24" strokeWidth="2"></mat-spinner>
        </div>
      </ng-container>

      <glxy-empty-state *ngIf="(loadingStatus$ | async) === LoadingStatus.ERROR">
        <glxy-empty-state-hero>
          <mat-icon>report_problem</mat-icon>
        </glxy-empty-state-hero>
        <glxy-empty-state-title>
          {{ 'AUXILIARY_DATA_CARD.ERROR.TITLE' | translate }}
        </glxy-empty-state-title>
        <p>{{ 'AUXILIARY_DATA_CARD.ERROR.TEXT' | translate }}</p>
        <glxy-empty-state-actions></glxy-empty-state-actions>
      </glxy-empty-state>
    </ng-template>
  `,
  styleUrls: ['./auxiliary-data-fields.component.scss'],
  standalone: false,
})
export class AuxiliaryDataFieldsComponent implements OnInit, OnDestroy {
  @Input() objectId: string;
  @Input() partnerId: string;
  @Input() objectType: ObjectType;
  @Input() numberOfFieldsToShow = SHOW_ALL_FIELDS;
  @Input() auxiliaryDataAdminURL = '';
  @Input() readOnly = false;
  @Input() reload$$: Subject<void> = new Subject();
  @Input() emptyStateIcon = 'topic';
  @Input() emptyStateTitle = 'AUXILIARY_DATA_TABLE.EMPTY.TITLE';
  @Input() emptyStateText = 'AUXILIARY_DATA_TABLE.EMPTY.TEXT';

  LoadingStatus = LoadingStatus;

  formGroup = new UntypedFormGroup({});
  loadingStatus$ = this.auxiliaryDataService.loadingStatus$;
  destroyed$$ = new Subject<void>();

  fieldHandlers$!: Observable<FieldHandler[]>;

  constructor(private readonly auxiliaryDataService: AuxiliaryFieldHandlerService) {}

  ngOnInit(): void {
    this.loadFieldHandlers();
    this.reload$$.pipe(takeUntil(this.destroyed$$)).subscribe(() => this.loadFieldHandlers());
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
    this.destroyed$$.unsubscribe();
  }

  private loadFieldHandlers(): void {
    this.auxiliaryDataService.init(this.partnerId, this.objectType, this.objectId, true, this.readOnly);
    this.fieldHandlers$ = this.auxiliaryDataService.fieldHandlers$.pipe(shareReplay({ refCount: true, bufferSize: 1 }));
    this.fieldHandlers$.pipe(takeUntil(this.destroyed$$)).subscribe((handlers) => {
      handlers.forEach((handler) => {
        this.formGroup.setControl(handler.fieldId, handler.formControl);
      });
    });
  }
}

@NgModule({
  imports: [
    CommonModule,
    TranslateModule.forChild(),
    ReactiveFormsModule,
    AuxiliaryDataModule,
    GalaxyEmptyStateModule,
    MatProgressSpinnerModule,
    MatIconModule,
  ],
  providers: [AuxiliaryFieldHandlerService],
  // AIM / SCAM patterns - only a single thing!
  declarations: [AuxiliaryDataFieldsComponent],
  exports: [AuxiliaryDataFieldsComponent],
})
export class AuxiliaryDataFieldsModule {}
